import type { Theme, SxProps } from '@mui/material/styles';

import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';

import { carouselClasses } from '../classes';

// ----------------------------------------------------------------------

export type CarouselDotVariant = 'circular' | 'rounded' | 'number';

/**
 * 점 줄. `scrollSnaps` 길이만큼 그린다.
 *
 * ⚠️ **필수 프롭이 없다** — 본문은 전부 `??`·`?.` 로 기본값을 채운다.
 *
 * ⚠️ 다만 `scrollSnaps` 없이 부르면 `.map` 에서, `onClickDot` 없이 누르면 호출에서 터진다
 * — 실호출은 셋을 언제나 함께 준다(A508(후보 A632)).
 *
 * ⚠️ 크기·간격 기본값이 갈래마다 다르다 — `number` 만 28px·간격 6, 나머지는 18px·간격 2.
 */
export type CarouselDotButtonsProps = {
  sx?: SxProps<Theme>;
  /** `carousel.dots.scrollSnaps` */
  scrollSnaps?: number[];
  selectedIndex?: number;
  onClickDot?: (index: number) => void;
  /** 기본 `'circular'` */
  variant?: CarouselDotVariant;
  /** 점 사이 간격(px) */
  gap?: number;
  slotProps?: { dot?: { size?: number; sx?: SxProps<Theme> } };
  className?: string;
};

export function CarouselDotButtons({
  sx,
  gap,
  slotProps,
  className,
  onClickDot,
  scrollSnaps,
  selectedIndex,
  variant = 'circular',
  ...other
}: CarouselDotButtonsProps) {
  const GAPS = { rounded: gap ?? 2, circular: gap ?? 2, number: gap ?? 6 };

  const SIZES = {
    circular: slotProps?.dot?.size ?? 18,
    rounded: slotProps?.dot?.size ?? 18,
    number: slotProps?.dot?.size ?? 28,
  };

  return (
    <Box
      component="ul"
      className={mergeClasses([carouselClasses.dots.root, className])}
      sx={[
        () => ({
          gap: `${GAPS[variant]}px`,
          height: SIZES[variant],
          zIndex: 9,
          display: 'flex',
          '& > li': {
            display: 'inline-flex',
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {scrollSnaps!.map((_, index) => {
        const selected = index === selectedIndex;

        return (
          <li key={index}>
            <DotItem
              disableRipple
              aria-label={`dot-${index}`}
              variant={variant}
              selected={selected}
              className={mergeClasses(carouselClasses.dots.item, {
                [carouselClasses.dots.itemSelected]: selected,
              })}
              onClick={() => onClickDot!(index)}
              sx={[
                () => ({
                  width: SIZES[variant],
                  height: SIZES[variant],
                }),
                ...(Array.isArray(slotProps?.dot?.sx)
                  ? (slotProps?.dot?.sx ?? [])
                  : [slotProps?.dot?.sx]),
              ]}
            >
              {variant === 'number' && index + 1}
            </DotItem>
          </li>
        );
      })}
    </Box>
  );
}

// ----------------------------------------------------------------------

const DotItem = styled(ButtonBase, {
  shouldForwardProp: (prop: string) => !['variant', 'selected', 'sx'].includes(prop),
})<{ variant?: CarouselDotVariant; selected?: boolean }>(({ selected, theme }) => {
  const dotStyles = {
    width: 8,
    height: 8,
    content: '""',
    opacity: 0.24,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    transition: theme.transitions.create(['width', 'opacity'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
  };

  return {
    variants: [
      {
        props: { variant: 'circular' },
        style: { '&::before': { ...dotStyles, ...(selected && { opacity: 1 }) } },
      },
      {
        props: { variant: 'rounded' },
        style: {
          '&::before': {
            ...dotStyles,
            ...(selected && {
              opacity: 1,
              width: 'calc(100% - 4px)',
              borderRadius: theme.shape.borderRadius,
            }),
          },
        },
      },
      {
        props: { variant: 'number' },
        style: {
          ...theme.typography.caption,
          borderRadius: '50%',
          color: theme.vars.palette.text.disabled,
          border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.16)}`,
          ...(selected && {
            color: theme.vars.palette.common.white,
            backgroundColor: theme.vars.palette.text.primary,
            fontWeight: theme.typography.fontWeightSemiBold,
            ...theme.applyStyles('dark', {
              color: theme.vars.palette.grey[800],
            }),
          }),
        },
      },
    ],
  };
});
